<script setup lang="ts">import { createPropsRestProxy } from '/vue-macros/reactivity-transform/helper';
const props = createPropsRestProxy(__props, ["file","isVisible","scale"]);
const rawIsVisible = __MACROS_toRef(__props, "isVisible");

import { toRef as __MACROS_toRef } from "vue";
import WaveSurfer from "wavesurfer.js";
import Hover from "wavesurfer.js/dist/plugins/hover.esm.js";
import Regions, { type Region, type RegionParams } from "wavesurfer.js/dist/plugins/regions.esm.js";

import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuRadioGroup,
  DropdownMenuSubContent,
  DropdownMenuRadioItem,
  DropdownMenuItemIndicator,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "reka-ui";
import { throttle } from "underscore";
import { useRouter } from "vue-router";
import { useMachine } from "@xstate/vue";
import { computed, nextTick, onMounted, onBeforeUnmount, provide, ref, watch, useTemplateRef, inject } from "vue";

import ConfirmPopover from "@/components/ConfirmPopover.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import VoicePlayerRedaction from "@/components/CasePanels/VoicePlayer/VoicePlayerRedaction.vue";
import VoiceTranscriptPanelAnnotations from "@/components/CasePanels/RecordView/VoiceTranscript/VoiceTranscriptPanelAnnotations.vue";

import {
  $can,
  isTimeIndexRedaction,
  isTranscriptAnnotation,
  toDuration,
  toDurationISO8601Text,
  toDurationHumanReadableText,
} from "@/services/UtilitiesService";
import { caseFileService } from "@/services";
import { isRedaction } from "@/services/AnnotationService";

import { useWindow } from "@/hooks/use-window";
import { CASE_ROUTE_PARAMS_KEY } from "@/hooks/use-case-route-params";

import { useBrandStore } from "@/stores/brand-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useVoiceMachineStore } from "@/stores/voice-machine-store";

import { Panels } from "@/models/PanelEnums";

import { voicePlayerMachine, type KnownRegionTypes } from "@/machines/voice-player-machine";

import type { ParsedMark } from "@/models/Mark";
import type { Metadata } from "@/models/Metadata";
import type { InjectedCaseRouteParams } from "@/hooks/use-case-route-params";
import type { TimeIndexRedaction, TranscriptAnnotation } from "@/models/Annotation";

withDefaults(defineProps<{
  isPopout: boolean;
  isVisible: boolean;
  file: Metadata;
  routeParams: InjectedCaseRouteParams;
  scale: { width: number; height: number };
}>(), {  })

const CSP_NONCE = inject<string>("NONCE");

const router = useRouter();
const window = useWindow();

const { send, snapshot, actorRef } = useMachine(voicePlayerMachine, {
  id: `VoicePlayer (${__props.file.fileReference})`,
  input: {
    client: props.routeParams.client,
    brand: props.routeParams.brand,
    caseReference: props.routeParams.casename,
    fileReference: __props.file.fileReference,
  },
});

const playerRef = useTemplateRef<HTMLElement | null>("wavesurferContainer");
const mediaRef = useTemplateRef<HTMLMediaElement | null>("mediaRef");

const pauseTargetRefs = ref<Array<HTMLElement | undefined>>([]);
const redactionTooltipRefs = ref<Array<HTMLElement | undefined>>([]);
const renderedRegionRefs = ref<Partial<Record<KnownRegionTypes, Map<string, Region>>>>({});

const shouldTrackAudio = ref(true);
const setTimeRangeEventReceived = ref(0);

const audioFileMetadata = ref<{ url: string; peaks: number[] }>();

const fieldRegionInUnsubscribe = ref<() => void>();
const fieldRegionOutUnsubscribe = ref<() => void>();
const selectedFieldBookmarkRegions = ref<Array<{ id: string; start: number; end: number }>>();

const isRedactionDialogOpen = ref(false);

const regionTitlesByTime = ref<Array<{ id: string; start: number; end: number; titles: string[] }>>([]);

const activePanel = ref<Record<string, boolean>>({});

const isPopout = computed(() => props.isPopout);
const routeParams = computed(() => props.routeParams);

const isVisible = computed(() => __props.isVisible);
const regions = computed(() => snapshot.value.context.regions ?? {});
const mainSurfer = computed(() => snapshot.value.context.surfer);
const isAudioReady = computed(() => snapshot.value.matches("ready"));

const fileMetadata = computed(
  () =>
    useCaseFileStore().cachedFilesData[__props.file.fileReference]?.metadata ?? {
      status: "loading" as const,
      data: undefined,
    },
);

const pauses = computed(() => fileMetadata.value.data?.audio?.pauses ?? []);

const voiceFileReference = computed(() => useCaseFileStore().cachedFilesReferences[__props.file.fileReference]?.voice);

const redactionRaw = computed(
  () => useCaseFileStore().cachedFilesData[__props.file.fileReference]?.extendedMetadata?.redactions,
);

const redactions = computed(() => {
  if (redactionRaw.value == null) return;
  return JSON.parse(redactionRaw.value) as Array<Record<"startTime" | "endTime", number>>;
});

const selectedAnnotation = computed(() => {
  if (routeParams.value.casefile !== __props.file.fileReference || routeParams.value.annotation == null) return;

  const annotation = useAnnotationStore().annotations.find((a) => a.markId === routeParams.value.annotation);
  if (
    annotation?.parsedMarkData == null ||
    !(isTranscriptAnnotation(annotation.parsedMarkData) || isTimeIndexRedaction(annotation.parsedMarkData))
  )
    return;

  return annotation as ParsedMark<TranscriptAnnotation> | ParsedMark<TimeIndexRedaction>;
});

const fileAnnotations = computed(() => {
  const isTimeIndexMark = (mark: ParsedMark): mark is ParsedMark<TimeIndexRedaction> =>
    isTimeIndexRedaction(mark.parsedMarkData);
  const isTranscriptMark = (mark: ParsedMark): mark is ParsedMark<TranscriptAnnotation> =>
    isTranscriptAnnotation(mark.parsedMarkData);

  const aiMarks: Array<ParsedMark<TranscriptAnnotation>> = [];
  const acceptedMarks: Array<ParsedMark<TranscriptAnnotation> | ParsedMark<TimeIndexRedaction>> = [];

  for (const mark of useAnnotationStore().unfilteredAnnotations) {
    if (mark.fileReference !== __props.file.fileReference) continue;

    const isTimeIndex = isTimeIndexMark(mark);
    const isTranscript = isTranscriptMark(mark);

    if (
      (isTimeIndex || isTranscript) &&
      (mark.source === "user" || (mark.evidenceThemes ?? [])?.some((e) => /accepted/i.test(e.status)))
    )
      acceptedMarks.push(mark);
    else if (isTranscript && mark.source === "ai") aiMarks.push(mark);
  }

  return { aiMarks, acceptedMarks };
});

const hasRedactions = computed(
  () =>
    useAnnotationStore().unfilteredAnnotations.find(
      (a) => a.parsedMarkData != null && a.fileReference === __props.file.fileReference && isRedaction(a.parsedMarkData),
    ) != null,
);

const playbackSpeedSteps = computed(() => {
  const MAX_PLAYBACK_SPEED = 3;
  const MIN_PLAYBACK_SPEED = 0.25;
  const PLAYBACK_SPEED_STEP = MIN_PLAYBACK_SPEED;

  return Array.from({ length: MAX_PLAYBACK_SPEED / PLAYBACK_SPEED_STEP }).map(
    (_, idx) => idx * MIN_PLAYBACK_SPEED + PLAYBACK_SPEED_STEP,
  );
});

const selectedThemes = computed(() => {
  const selected = useCaseFileStore().selectedThemes;
  if (selected == null) return;

  const result = new Set<string>();
  const hiddenThemes = useBrandStore().viewerConfig.evidenceThemes?.hiddenThemes ?? {};

  for (const theme of selected) {
    if (hiddenThemes[theme] === true) continue;
    result.add(theme);
  }

  return result;
});

const selectedFieldBookmarks = computed(() => {
  if (routeParams.value.casefile !== __props.file.fileReference || routeParams.value.fieldPath == null) return;

  const fileForm = useCaseFileStore().cachedFilesData[__props.file.fileReference]?.form;
  if (fileForm?.status !== "success" || fileForm?.form == null) return;

  const editEvents = fileForm.form.answers.__editEvents ?? [];
  return editEvents.filter((event) => event.path === `/${routeParams.value.fieldPath}`);
});

const currentPanelGlobal = computed(() => {
  const state = useCaseFileStore().currentPanelGlobal;
  return { ...state.audio, ...state.shared };
});

watch(
  () => __props.scale,
  () => nextTick(() => send({ type: "PLAYER_RE_RENDER" })),
);

watch(
  () => routeParams.value.casefile,
  (newCaseFile) => {
    if (newCaseFile === __props.file.fileReference) return;
    isRedactionDialogOpen.value = false;
    send({ type: "PLAYER_PAUSE" });
  },
);

watch([voiceFileReference, fileMetadata, mainSurfer], async () => {
  const { client, brand, casename, casefile } = routeParams.value;

  if (fileMetadata.value.status !== "success" || mainSurfer.value == null) return;
  if (casefile !== __props.file.fileReference || voiceFileReference.value == null) return;

  if (snapshot.value.matches("ready")) return;

  send({ type: "PLAYER_IDLE" });

  if (audioFileMetadata.value == null) {
    const [url, peaks] = await Promise.all([
      caseFileService.get(client, brand, casename, voiceFileReference.value, true),
      caseFileService.getSupportingType(client, brand, casename, voiceFileReference.value, "peak").catch((e) => {
        console.error(e);
        return [];
      }),
    ]);
    audioFileMetadata.value = { url, peaks };
  }

  send({
    type: "PLAYER_LOAD_AUDIO",
    ...audioFileMetadata.value,
    metadata: { client, brand, caseReference: casename, fileReference: casefile },
  });
});

watch([regions, pauses], () => regions.value.pauses != null && renderRegions("pauses"));
watch([regions, redactions], () => regions.value.redactions != null && renderRegions("redactions"));
watch([regions, fileAnnotations, selectedThemes], () => regions.value.marks != null && renderRegions("marks"));
watch([regions, selectedFieldBookmarks], () => regions.value.formFieldEdits != null && renderRegions("formFieldEdits"));

const onRegionsInitializedFinish = watch([isVisible, isAudioReady], () => {
  if (!isAudioReady.value || !isVisible.value) return;

  // Initialize Regions in order since the created container's zIndex is not customizable

  if (regions.value.formFieldEdits == null)
    send({ type: "PLAYER_ADD_REGIONS_PLUGIN", name: "formFieldEdits", regions: Regions.create() });

  if (regions.value.marks == null)
    send({ type: "PLAYER_ADD_REGIONS_PLUGIN", name: "marks", regions: Regions.create() });

  if (regions.value.redactions == null) {
    const redactionsRegions = Regions.create();

    redactionsRegions.on("region-in", (region) => region.setOptions({ color: "rgba(0, 0, 0, 0.5)" }));
    redactionsRegions.on("region-out", (region) => region.setOptions({ color: "rgba(0, 0, 0, 1)" }));

    send({ type: "PLAYER_ADD_REGIONS_PLUGIN", name: "redactions", regions: redactionsRegions });
  }

  if (regions.value.pauses == null)
    send({ type: "PLAYER_ADD_REGIONS_PLUGIN", name: "pauses", regions: Regions.create() });

  onRegionsInitializedFinish();
});

watch(selectedFieldBookmarkRegions, () => {
  const onPlayBackEnd = () => {
    fieldRegionInUnsubscribe.value?.();
    fieldRegionOutUnsubscribe.value?.();
    send({ type: "PLAYER_PAUSE" });
  };

  onPlayBackEnd();

  const fieldEditRegions = regions.value.formFieldEdits;

  if (
    fieldEditRegions == null ||
    selectedFieldBookmarkRegions.value == null ||
    selectedFieldBookmarkRegions.value.length < 1
  )
    return;

  fieldRegionInUnsubscribe.value = fieldEditRegions.on("region-in", (inRegion) => {
    if (selectedFieldBookmarkRegions.value == null) {
      onPlayBackEnd();
      return;
    }

    // Validate the ID of the `region-in` event is part of the bookmarks to allow the subsequent
    // region-out to be triggered for the same region.
    const regionIdx = selectedFieldBookmarkRegions.value.findIndex(({ id }) => id === inRegion.id);
    if (regionIdx < 0) {
      onPlayBackEnd();
      return;
    }

    // Remove the previous listener
    fieldRegionOutUnsubscribe.value?.();

    fieldRegionOutUnsubscribe.value = fieldEditRegions.on("region-out", (outRegion) => {
      // Verify the region IDs match
      if (inRegion.id !== outRegion.id) return;

      // Remove this listener, to allow the next one continue the loop
      fieldRegionOutUnsubscribe.value?.();

      if (selectedFieldBookmarkRegions.value == null) {
        onPlayBackEnd();
        return;
      }

      if (
        // A different region was played?
        regionIdx < 0 ||
        // The last region in the bookmark has played (maybe loop to the start here)
        regionIdx + 1 >= selectedFieldBookmarkRegions.value.length
      ) {
        onPlayBackEnd();
        return;
      }

      manualJump(selectedFieldBookmarkRegions.value[regionIdx + 1].start);
    });
  });

  manualJump(selectedFieldBookmarkRegions.value[0].start);
});

watch(
  [routeParams, isAudioReady, () => selectedAnnotation.value?.parsedMarkData.uuid],
  ([{ annotation, startTime, casefile, fieldPath, time }]) => {
    if (casefile !== __props.file.fileReference) return;

    if (startTime != null || time != null) {
      const seconds = +(startTime ?? time ?? -1);
      if (seconds > -1) manualJump(seconds);
    } else if (annotation != null) {
      const seconds = selectedAnnotation.value?.parsedMarkData.startTime ?? -1;
      if (seconds > -1) manualJump(seconds);
    } else if (fieldPath != null) {
      /* will be handled with the watcher on the field bookmarks ref */
    }
  },
);

provide(CASE_ROUTE_PARAMS_KEY, routeParams);

useVoiceMachineStore().$onAction(({ name, args }) => {
  if (args[1] !== __props.file.fileReference) return;

  if (name === "setShouldTrackAudio") shouldTrackAudio.value = args[2];

  if (name === "setTimeRange" && args[0] !== "player") {
    manualJump(args[2]);
    setTimeRangeEventReceived.value = args[2];
  }
});

onMounted(() => {
  const media = mediaRef.value;
  const container = playerRef.value;
  if (container == null || media == null) return;

  const surfer = WaveSurfer.create({
    container,
    height: "auto", // Must be specified
    cursorColor: "#000",
    waveColor: "#636463",
    progressColor: "#636463",
    media,
    backend: "MediaElement",
    cspNonce: CSP_NONCE,
    plugins: [
      Hover.create({
        lineWidth: 2,
        labelColor: "#fff",
        labelBackground: "#555",
        labelSize: "10px",
        formatTimeCallback: throttle((secs: number) => {
          const text = toDurationHumanReadableText(secs, true);

          const titles = new Set<string>();

          // Binary search to find the first region with start >= secs
          let left = 0;
          let right = regionTitlesByTime.value.length;
          while (left < right) {
            const mid = Math.floor((left + right) / 2);
            if (regionTitlesByTime.value[mid].start < secs) {
              left = mid + 1;
            } else {
              right = mid;
            }
          }

          // Walk backwards from binary search result to find regions that overlap secs
          for (let i = left - 1; i >= 0; i--) {
            const region = regionTitlesByTime.value[i];
            if (region.end <= secs) break; // No more overlapping regions
            if (secs >= region.start && secs < region.end) {
              region.titles.forEach((t) => titles.add(t));
            }
          }

          return titles.size === 0 ? text : `${[...titles].join(", ")} (${text})`;
        }, 50),
      }),
    ],
  });

  surfer.once("init", () => {
    send({ type: "PLAYER_SET_SURFER", surfer });
  });

  surfer.on("decode", () => {
    const shadow = container.firstElementChild?.shadowRoot;
    if (shadow == null) return;

    const regionsStylesheet = new CSSStyleSheet();
    for (const sheet of window.value.document.styleSheets) {
      if (sheet.href == null) continue;
      const sheetLinkEl = Object.assign(window.value.document.createElement("link"), {
        rel: "stylesheet",
        href: sheet.href,
      });
      shadow.appendChild(sheetLinkEl);
    }
    shadow.adoptedStyleSheets.push(regionsStylesheet);

    regionsStylesheet.replaceSync(
      `
        [part=wrapper] [part*=region-marker] { z-index: 10 !important; }
        [part=wrapper] [part*=top-region] { height: 50% !important; }
        [part=wrapper] [part*=bottom-region] { height: 50% !important; top: 50% !important; }
        [part=wrapper] [part*=field-bookmark-region] { height: 25% !important; top: 62.5% !important; }
        [part*=region] { transition: none !important; }
        :host .scroll { height: 100%; }
        :host .cursor { z-index: 10000; border: 1px solid #000; border-radius: 0; }
        `,
    );
  });

  surfer.on("interaction", onAudioInteraction);

  surfer.on("error", (er) => {
    const error = new Error(
      (er instanceof MediaError
        ? er.code === er.MEDIA_ERR_DECODE || er.code === er.MEDIA_ERR_SRC_NOT_SUPPORTED
          ? "Your browser does not support this audio format."
          : undefined
        : undefined) ??
        (er.message || "An unknown error occurred during audio playback."),
    );

    console.error(error);
    send({ type: "PLAYER_ERROR", message: error.message });

    const session = fileMetadata.value.data?.session;
    if (session != null) send({ type: "PLAYER_CHANGE_DURATION", value: session.recordedTimeInMilliSeconds / 1000 });
  });
});

onBeforeUnmount(() => {
  if (actorRef.getSnapshot().status !== "stopped") send({ type: "PLAYER_DESTROY" });
});

const onTimeUpdate = throttle(() => {
  if (!shouldTrackAudio.value || mediaRef.value == null) return;

  const currentTime = +mediaRef.value.currentTime.toFixed(2);
  const receivedTime = +setTimeRangeEventReceived.value.toFixed(2);
  if (currentTime === receivedTime) return;

  useVoiceMachineStore().setTimeRange("player", __props.file.fileReference, currentTime);
}, 250);

function onAudioInteraction(seconds: number) {
  useVoiceMachineStore().setShouldTrackAudio("player", __props.file.fileReference, true);

  router.push({
    name: "casefiletime",
    params: {
      brand: routeParams.value.brand,
      casename: routeParams.value.casename,
      casefile: __props.file.fileReference,
      time: seconds.toFixed(2),
    },
  });
}

function manualJump(seconds: number) {
  useVoiceMachineStore().setShouldTrackAudio("player", __props.file.fileReference, true);
  if (Math.abs(snapshot.value.context.elapsed - seconds) > 0.05) send({ type: "PLAYER_CHANGE_POSITION", seconds });
}

function renderRegions(regionType: KnownRegionTypes) {
  renderedRegionRefs.value[regionType] ??= new Map();

  const renderedRegions = renderedRegionRefs.value[regionType];
  const renderedRegionIDs = new Set(renderedRegions.keys());

  function renderRegion(id: string, opts: Omit<RegionParams, "id">, opacity = 1) {
    if (regions.value[regionType] == null) throw new Error(`"${regionType}" Region plugin not registered`);

    if (!renderedRegions.has(id)) {
      renderedRegions.set(
        id,
        regions.value[regionType].addRegion({ id, start: opts.start, resize: false, drag: false }),
      );
    }

    const color = `${opts.color}${Math.ceil(opacity * 255).toString(16)}`;
    renderedRegions.get(id)?.setOptions({ ...opts, color });

    // Remove ID to account for this region
    renderedRegionIDs.delete(id);
  }

  function canShowTheme(e: { evidenceTheme: string }) {
    return selectedThemes.value == null || selectedThemes.value.has(e.evidenceTheme);
  }

  switch (regionType) {
    case "marks": {
      const { acceptedMarks, aiMarks } = fileAnnotations.value;
      const summaryHighlightColors = useBrandStore().viewerConfig.summaryThemes?.highlightColors;
      const evidenceHighlightColors = useBrandStore().viewerConfig.evidenceThemes?.highlightColors;

      const OPACITY = 0.5;
      const getId = (side: "top" | "bottom", markId: string) => `${side}-region-${markId}`;

      // Rebuild sorted array for hover lookup
      regionTitlesByTime.value = [];

      if (summaryHighlightColors != null) {
        aiMarks.forEach((mark) => {
          const { uuid, startTime: start, endTime: end } = mark.parsedMarkData;

          const themes = mark.evidenceThemes
            ?.filter((e) => canShowTheme(e) && !/rejected/i.test(e.status))
            .map((e) => e.evidenceTheme);
          if (themes == null || themes.length === 0) return;

          const id = getId("top", uuid);
          const color = summaryHighlightColors[themes[themes.length - 1]];

          renderRegion(id, { start, end, color }, OPACITY);
          regionTitlesByTime.value.push({ id, start, end, titles: themes });
        });
      }

      acceptedMarks.forEach((mark) => {
        const { uuid, startTime: start, endTime: end } = mark.parsedMarkData;
        const id = getId("bottom", uuid);

        const titles: string[] = [];
        regionTitlesByTime.value.push({ id, start, end, titles });

        if (mark.parsedMarkData.type === "TranscriptRedaction" || mark.parsedMarkData.type === "TimeIndexRedaction") {
          titles.push("Pending Redaction");
          renderRegion(id, { start, end, color: "#000000" }, OPACITY);
        } else if (mark.evidenceThemes == null || mark.evidenceThemes.length === 0)
          renderRegion(id, { start, end, color: "#ffff00" }, OPACITY);
        else {
          for (const e of mark.evidenceThemes) {
            if (!canShowTheme(e)) continue;
            titles.push(e.evidenceTheme);
          }

          if (titles.length > 0) {
            const color = evidenceHighlightColors?.[titles[titles.length - 1]] ?? "#ffff00";
            renderRegion(id, { start, end, color }, OPACITY);
          }
        }
      });

      // Sort by start time for binary search
      regionTitlesByTime.value.sort((a, b) => a.start - b.start);

      break;
    }
    case "pauses": {
      pauses.value.forEach((pause, idx) =>
        renderRegion(
          `region-marker-${pause.elapsedTimeInAudio}`,
          { start: pause.elapsedTimeInAudio, content: pauseTargetRefs.value[idx] },
          0.2,
        ),
      );

      break;
    }
    case "redactions": {
      redactions.value?.forEach(({ startTime: start, endTime: end }, idx) =>
        renderRegion(`field-redaction-region-${start}-${end}`, {
          start,
          end,
          color: "#000000",
          content: redactionTooltipRefs.value[idx],
        }),
      );

      break;
    }
    case "formFieldEdits": {
      const createdTime = Date.now();

      selectedFieldBookmarkRegions.value = selectedFieldBookmarks.value?.map(({ path, startTime, endTime }, idx) => {
        // Use the created time as part of ID to invalidate previous in/out events
        const id = `field-bookmark-region-${path}-${idx}-${createdTime}`;

        renderRegion(id, { color: "#00FF00", start: startTime, end: endTime }, 0.5);

        return { id, start: startTime, end: endTime };
      });
      break;
    }
  }

  // Delete unaccounted-for regions from the UI
  for (const id of renderedRegionIDs) {
    renderedRegions.get(id)?.remove();
    renderedRegions.delete(id);
  }
}

async function startAudioRedaction(deselectAnnotation: boolean) {
  if (deselectAnnotation && routeParams.value.annotation != null) {
    const { brand, casename, casefile } = routeParams.value;
    await router.push({ name: "casefile", params: { brand, casename, casefile } });
  }

  isRedactionDialogOpen.value = true;
  send({ type: "PLAYER_PAUSE" });
}

function togglePanel(panel: string) {
  if (activePanel.value[panel]) {
    activePanel.value[panel] = false;
    useCaseFileStore().currentTranscriptPanelGlobalChanged(panel, true);
  } else if (!activePanel.value[panel]) {
    activePanel.value[panel] = !currentPanelGlobal.value[panel];
    useCaseFileStore().currentTranscriptPanelGlobalChanged(panel, false);
  }
}

async function commitRedaction() {
  if (isPopout.value) return;

  try {
    await useAnnotationStore().redact({ fileReference: __props.file.fileReference });

    // Deselect the selected annotation if it is to be redacted
    const selectedAnnotation =
      routeParams.value.annotation == null
        ? undefined
        : useAnnotationStore().annotations.find((a) => a.markId === routeParams.value.annotation);

    if (selectedAnnotation != null) {
      try {
        if (
          isTranscriptAnnotation(selectedAnnotation.parsedMarkData) &&
          selectedAnnotation.parsedMarkData.type === "TranscriptRedaction"
        ) {
          router.push({
            name: "casefile",
            params: {
              brand: routeParams.value.brand,
              casename: routeParams.value.casename,
              casefile: __props.file.fileReference,
            },
          });
        }
      } catch (error) {}
    }

    useCaseFileStore().setCommittedRedactionsState(__props.file.fileReference, true);
  } catch {}
}
</script>
<template>
  <div
    v-if="fileMetadata.status === 'not-exists' || (fileMetadata.data != null && fileMetadata.data.audio == null)"
    class="tw-grid tw-size-full tw-place-items-center"
  >
    <div class="tw-max-w-sm tw-text-pretty">No audio exists for this file</div>
  </div>
  <div
    v-else
    class="tw-relative tw-grid tw-size-full tw-min-h-0 tw-grow tw-grid-cols-[max-content_1fr] tw-grid-rows-[1fr_max-content] tw-overflow-y-auto [grid-template-areas:'panels_container'_'panels_controls']"
  >
    <div
      ref="wavesurferContainer"
      class="tw-relative tw-col-start-1 tw-col-end-2 tw-row-start-1 tw-row-end-2 tw-min-h-0 tw-overflow-hidden [grid-area:container] *:tw-h-full"
    />
    <div class="empty:hidden tw-contents *:[grid-area:container]">
      <div v-if="snapshot.matches('init')" class="tw-z-50 tw-grid tw-size-full tw-place-items-center tw-bg-white">
        Initializing...
      </div>
      <div
        v-else-if="snapshot.matches('loading') || fileMetadata.status == 'loading'"
        class="tw-z-50 tw-grid tw-size-full tw-place-items-center tw-bg-white"
      >
        Loading...
      </div>
      <div v-else-if="snapshot.matches('error')" class="tw-z-50 tw-grid tw-size-full tw-place-items-center tw-bg-white">
        {{ snapshot.context.error }}
      </div>
    </div>
    <div
      class="tw-z-10 tw-grid tw-min-h-0 tw-auto-rows-max tw-gap-2 tw-bg-gray-300 tw-py-1 tw-@container [grid-area:controls] sm:tw-py-2"
    >
      <!-- Our method of loading the audio through wavesurfer doesn't allow setting the crossorigin attribute. So we must set the external media element -->
      <audio ref="mediaRef" crossorigin="use-credentials" class="tw-sr-only" @timeupdate="onTimeUpdate" />
      <input
        type="range"
        min="0"
        :max="snapshot.context.duration.toFixed(2)"
        step="0.01"
        class="tw-w-full"
        :value="snapshot.context.elapsed.toFixed(2)"
        aria-label="Audio Timeline"
        :aria-valuetext="`${toDurationHumanReadableText(snapshot.context.elapsed)} played`"
        @input="(event) => manualJump((event.target as HTMLInputElement).valueAsNumber)"
        @change="(event) => onAudioInteraction((event.target as HTMLInputElement).valueAsNumber)"
      />
      <div
        class="tw-flex tw-flex-col tw-flex-wrap tw-items-center tw-gap-2 tw-px-2 tw-text-xl @md:tw-grid @md:tw-grid-cols-[minmax(0,1fr)_max-content_minmax(0,1fr)] [&>*]:tw-min-w-0"
      >
        <div
          tabindex="0"
          class="tw-flex tw-flex-wrap tw-items-center tw-justify-evenly tw-gap-x-3 tw-gap-y-1 tw-break-words tw-font-medium md:tw-text-sm"
          :aria-label="`${toDurationHumanReadableText(snapshot.context.elapsed)} of ${toDurationHumanReadableText(snapshot.context.duration)} played`"
        >
          <button
            type="button"
            aria-label="Open Marks Panel"
            class="tw-btn-md-gray tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-xs"
            :class="{
              '!tw-shadow': activePanel[Panels.shared.ANNOTATIONS],
              invert: !currentPanelGlobal[Panels.shared.ANNOTATIONS],
            }"
            @click="() => togglePanel(Panels.shared.ANNOTATIONS)"
          >
            <i aria-hidden="true" class="fas fa-pen" />
            <span>Marks</span>
          </button>
          <div class="tw-font-mono tw-text-[0.65rem]">
            <time :datetime="toDurationISO8601Text(snapshot.context.elapsed)">
              {{ toDuration(snapshot.context.elapsed) }}
            </time>
            &nbsp;/&nbsp;
            <time :datetime="toDurationISO8601Text(snapshot.context.duration)">
              {{ toDuration(snapshot.context.duration) }}
            </time>
          </div>
        </div>
        <div class="tw-flex tw-items-center tw-gap-x-1">
          <StringTooltip side="bottom" align="end" item="Skip Backward">
            <button
              aria-label="Skip Backward"
              class="tw-size-7 tw-rounded-full tw-p-1 tw-text-purple-branddark hover:tw-bg-purple-branddark/10"
              @click="send({ type: 'PLAYER_SKIP_BACKWARD' })"
            >
              <i class="fas fa-backward"></i>
            </button>
          </StringTooltip>
          <template v-for="(isPlaying, idx) in [snapshot.matches({ ready: 'playing' })]" :key="idx">
            <StringTooltip side="bottom" align="start" :item="isPlaying ? 'Pause Audio' : 'Play Audio'">
              <button
                class="tw-size-9 tw-p-1 tw-text-2xl tw-text-purple-branddark"
                :aria-pressed="isPlaying"
                :aria-label="isPlaying ? 'Pause Audio' : 'Play Audio'"
                @click="send({ type: isPlaying ? 'PLAYER_PAUSE' : 'PLAYER_PLAY' })"
              >
                <i v-if="isPlaying" class="far fa-pause-circle" />
                <i v-else class="far fa-play-circle" />
              </button>
            </StringTooltip>
          </template>
          <StringTooltip side="bottom" align="start" item="Skip Forward">
            <button
              aria-label="Skip Forward"
              class="tw-size-7 tw-rounded-full tw-p-1 tw-text-purple-branddark hover:tw-bg-purple-branddark/10"
              @click="send({ type: 'PLAYER_SKIP_FORWARD' })"
            >
              <i class="fas fa-forward"></i>
            </button>
          </StringTooltip>
        </div>
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-3 tw-gap-y-1">
          <DropdownMenuRoot>
            <DropdownMenuTrigger
              class="tw-btn-md-gray invert tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-text-xs"
            >
              More
              <i class="fas fa-ellipsis fa-sm" />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent
                align="start"
                :side-offset="5"
                class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
              >
                <template v-if="$can('redact')">
                  <DropdownMenuItem
                    class="tw-cursor-pointer tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                    :disabled="!snapshot.matches('ready')"
                    @select="startAudioRedaction(true)"
                  >
                    New Audio Redaction
                  </DropdownMenuItem>
                  <DropdownMenuSeparator class="!tw-my-1 tw-h-px tw-w-full tw-bg-gray-500" />
                </template>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-4 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                  >
                    Playback Speed ({{ snapshot.context.speed }}x)
                    <i aria-hidden="true" class="fas fa-chevron-right"></i>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent
                      align="start"
                      :side-offset="10"
                      class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
                    >
                      <DropdownMenuRadioGroup
                        :model-value="snapshot.context.speed.toString()"
                        @update:model-value="(value) => send({ type: 'PLAYER_CHANGE_SPEED', value: +value })"
                      >
                        <DropdownMenuRadioItem
                          v-for="speed in playbackSpeedSteps"
                          :key="speed"
                          :value="speed.toString()"
                          class="tw-grid tw-cursor-pointer tw-grid-cols-[1rem_minmax(0,1fr)] tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                        >
                          <DropdownMenuItemIndicator>
                            <i class="fas fa-check fa-sm"></i>
                          </DropdownMenuItemIndicator>
                          <span class="tw-col-start-2">{{ speed }}x</span>
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    class="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-4 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                  >
                    Volume ({{ snapshot.context.volume * 100 }}%)
                    <i aria-hidden="true" class="fas fa-chevron-right"></i>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent
                      align="start"
                      :side-offset="10"
                      class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
                    >
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        class="tw-w-40"
                        aria-label="Volume Slider"
                        :value="snapshot.context.volume * 100"
                        @input="
                          (event) =>
                            send({
                              type: 'PLAYER_CHANGE_VOLUME',
                              value: +(+((event.target as HTMLInputElement)?.value ?? 100) / 100).toFixed(2),
                            })
                        "
                      />
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenuRoot>
          <button
            v-if="$can('redact') && isTimeIndexRedaction(selectedAnnotation?.parsedMarkData)"
            type="button"
            class="tw-btn-md-gray invert tw-min-w-max tw-text-xs"
            aria-label="Click to open the Audio Redaction dialog to view and edit the redaction mark."
            @click="() => startAudioRedaction(false)"
          >
            <i class="fas fa-pencil fa-sm"></i>
            Edit Audio Redaction
          </button>
          <ConfirmPopover
            v-if="$can('redact') && hasRedactions && !isPopout"
            confirm-text="Commit"
            :trigger-props="{ class: 'tw-btn-md-gray tw-min-w-max invert tw-text-xs' }"
            @confirmed="commitRedaction"
          >
            <template #trigger>
              <i aria-hidden="true" class="fas fa-eraser" />
              Commit Redactions
            </template>
            <template #question>
              Are you sure? This will commit the redaction masks to the record. The original audio uploaded will be
              permanently destroyed and replaced with the sections of audio silenced. Access to this record will be
              temporarily suspended whilst the redactions are being committed.
            </template>
          </ConfirmPopover>
        </div>
      </div>
    </div>
    <VoicePlayerRedaction
      v-if="
        $can('redact') &&
        isRedactionDialogOpen &&
        routeParams.casefile === file.fileReference &&
        audioFileMetadata != null &&
        (selectedAnnotation?.parsedMarkData == null || isTimeIndexRedaction(selectedAnnotation?.parsedMarkData))
      "
      :key="routeParams.annotation"
      v-model:open="isRedactionDialogOpen"
      :audio="audioFileMetadata"
      :file-reference="file.fileReference"
      :duration="snapshot.context.duration"
      :start-time="snapshot.context.elapsed"
      :annotation="selectedAnnotation?.parsedMarkData"
      :playback-speed-steps="playbackSpeedSteps"
    />
    <div class="min-w-[7.5rem] tw-flex tw-border-r tw-border-black/15 [grid-area:panels] empty:tw-hidden">
      <VoiceTranscriptPanelAnnotations
        v-if="activePanel[Panels.shared.ANNOTATIONS] || currentPanelGlobal[Panels.shared.ANNOTATIONS]"
        class="!tw-w-full"
        :annotation="routeParams.annotation"
        :file-reference="file.fileReference"
      />
    </div>
    <div v-show="snapshot.matches('ready')" class="empty:tw-hidden">
      <template v-for="(pause, idx) in pauses" :key="pause.startTime">
        <StringTooltip
          v-for="text in [
            `Pause of ${
              pause.duration > 60 ? Math.round(pause.duration / 60) + 'm' : Math.round(pause.duration) + 's'
            }. Reason: ${pause.reason}`,
          ]"
          :key="text"
          :item="text"
        >
          <button
            :ref="(el) => (pauseTargetRefs[idx] = (el ?? undefined) as HTMLElement | undefined)"
            type="button"
            class="tw-absolute tw-bottom-0 tw-translate-x-[calc(-50%-1px)] tw-text-2xl tw-leading-none tw-text-purple-branddark"
            @click.prevent.stop="() => send({ type: 'PLAYER_CHANGE_POSITION', seconds: pause.elapsedTimeInAudio })"
          >
            <!--
            The button itself can't be the item with the white background as there's a little bleed of white to the elements left.
            So this fixed positioned item that should be half the button's size will only cover the pause bars in the icon.
          -->
            <div
              style="height: 50%"
              class="tw-fixed tw-left-1/2 tw-top-1/2 tw-w-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-white"
            />
            <i class="fas fa-pause-circle tw-relative tw-z-10" />
            <span class="tw-sr-only">
              {{ text }}
            </span>
          </button>
        </StringTooltip>
      </template>
      <template v-for="(redaction, idx) in redactions" :key="redaction.startTime">
        <template v-for="duration in [redaction.endTime - redaction.startTime]">
          <StringTooltip
            v-for="text in [
              `Redacted audio of ${duration > 60 ? Math.round(duration / 60) + 'm' : Math.round(duration) + 's'}`,
            ]"
            :key="text"
            :item="text"
          >
            <div
              :ref="(el) => (redactionTooltipRefs[idx] = (el ?? undefined) as HTMLElement | undefined)"
              aria-hidden="true"
              class="tw-size-full"
            />
          </StringTooltip>
        </template>
      </template>
    </div>
  </div>
</template>
